import React from 'react';
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { LiveScoreboardEntry } from '../../../types/liveQuiz';
import { medalColor } from '../../ranked/tierColors';

interface LiveScoreboardProps {
  entries: LiveScoreboardEntry[];
  currentUserId?: number;
  title?: string;
}

/**
 * Between-question standings.
 *
 * The movement arrows are the point: seeing yourself climb two places is what
 * makes the scoreboard a moment rather than a table. Rows animate to their new
 * position via framer-motion layout animation, so a change in order reads as
 * movement instead of a repaint.
 */
const LiveScoreboard: React.FC<LiveScoreboardProps> = ({
  entries,
  currentUserId,
  title = 'Standings'
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper'
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: 'text.secondary'
        }}
      >
        {title}
      </Typography>

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <AnimatePresence initial={false}>
          {entries.map(entry => {
            const isYou = entry.userId === currentUserId;
            const medal = medalColor(entry.rank);

            return (
              <motion.div
                key={entry.userId}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '36px 1fr auto auto',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${isYou ? alpha(theme.palette.primary.main, 0.3) : 'transparent'}`,
                    bgcolor: isYou
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.action.hover, 0.03)
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                      textAlign: 'center',
                      color: medal ?? 'text.secondary'
                    }}
                  >
                    {entry.rank}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: isYou ? 700 : 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {entry.displayName}
                    {isYou && (
                      <Typography
                        component="span"
                        sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 700, color: 'primary.main' }}
                      >
                        YOU
                      </Typography>
                    )}
                  </Typography>

                  <MovementIndicator movement={entry.movement} />

                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                      minWidth: 48,
                      textAlign: 'right'
                    }}
                  >
                    {entry.score}
                  </Typography>
                </Box>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>
    </Paper>
  );
};

/** Positions gained or lost since the previous scoreboard. */
const MovementIndicator: React.FC<{ movement: number }> = ({ movement }) => {
  const theme = useTheme();

  if (movement === 0) {
    return <Box sx={{ width: 28 }} />;
  }

  const climbing = movement > 0;
  const color = climbing ? theme.palette.success.main : theme.palette.error.main;
  const Icon = climbing ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, width: 28, color }}>
      <Icon sx={{ fontSize: 14 }} />
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{Math.abs(movement)}</Typography>
    </Box>
  );
};

export default LiveScoreboard;
